import React, { useEffect, useMemo, useState } from "react";

import Breadcrumbs from "../../../components/Breadcrumbs";
import Topbar from "../../../components/Topbar";

import { parseCurrency } from "../../../../utils/SamuiUtils";
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions,
} from "../../../../utils/DataTableKeyboardNavigation";

const DEFAULT_THEME_COLOR = "#E13F57";

const tableHeaderStyle = {
  backgroundColor: DEFAULT_THEME_COLOR,
  color: "#fff",
  position: "sticky",
  top: 0,
  zIndex: 10,
  borderBottom: "2px solid rgba(0,0,0,0.08)",
};

const headerCellBaseStyle = {
  fontWeight: "600",
  color: "#fff",
};

const searchIconStyle = {
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#9ca3af",
};

const pageButtonStyle = (isActive, themeColor) => ({
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  color: isActive ? "#fff" : "#374151",
  backgroundColor: isActive ? themeColor : "#fff",
  fontSize: "14px",
  padding: "6px 12px",
  minWidth: "36px",
  textAlign: "center",
});

const uniqueOptions = (options) => {
  const seen = new Set();
  const result = [];
  options.forEach((value) => {
    if (value && !seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  });
  return result;
};

const InitialDataListPage = ({
  name,
  entityPath,
  data = [],
  serverSide = false,
  totalItems: totalItemsProp,
  currentPage: currentPageProp,
  itemsPerPage: itemsPerPageProp,
  onPageChange: onPageChangeProp,
  onItemsPerPageChange: onItemsPerPageChangeProp,
  searchTerm: searchTermProp,
  onSearchChange: onSearchChangeProp,
  onCreate,
  onRowSelected,
  columns = [],
  getSearchValues,
  searchPlaceholder,
  fieldMappings = {},
  getRowId,
  themeColor = DEFAULT_THEME_COLOR,
  itemsPerPageOptions = [50, 100, 150],
  extraData = {},
  isLoading = false,
  loadingMessage = "กำลังโหลดข้อมูล...",
  customHeader = null,
  hidePageTitle = false,
}) => {
  const masterList = Array.isArray(data) ? data : [];
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [itemsPerPageState, setItemsPerPageState] = useState(
    itemsPerPageOptions?.[0] || 50
  );
  const [currentPageState, setCurrentPageState] = useState(1);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const isServerSide = !!serverSide;
  const effectiveSearchTerm = isServerSide
    ? searchTermProp ?? ""
    : localSearchTerm;
  const currentPage = isServerSide ? currentPageProp ?? 1 : currentPageState;
  const itemsPerPage = isServerSide
    ? itemsPerPageProp ?? itemsPerPageState
    : itemsPerPageState;

  useEffect(() => {
    if (isServerSide) {
      setSelectedRowIndex(0);
      return;
    }
    setCurrentPageState(1);
    setSelectedRowIndex(0);
  }, [masterList.length, isServerSide]);

  const filteredItems = useMemo(() => {
    if (isServerSide) {
      return masterList;
    }
    const keyword = effectiveSearchTerm.trim().toLowerCase();
    if (!keyword) {
      return masterList;
    }

    const resolveSearchValues =
      typeof getSearchValues === "function"
        ? getSearchValues
        : (item) => Object.values(item || {});

    return masterList.filter((item) => {
      if (!item) {
        return false;
      }

      const values = resolveSearchValues(item, extraData);
      if (!Array.isArray(values)) {
        return false;
      }

      return values.some((value) => {
        if (value === null || value === undefined) {
          return false;
        }
        return String(value).toLowerCase().includes(keyword);
      });
    });
  }, [masterList, effectiveSearchTerm, getSearchValues, extraData, isServerSide]);

  useEffect(() => {
    if (isServerSide) {
      setSelectedRowIndex(0);
      return;
    }
    setCurrentPageState(1);
    setSelectedRowIndex(0);
  }, [filteredItems, isServerSide]);

  useEffect(() => {
    const totalCount = isServerSide
      ? totalItemsProp ?? 0
      : filteredItems.length;
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    if (currentPage > totalPages) {
      if (!isServerSide) {
        setCurrentPageState(totalPages);
      } else if (typeof onPageChangeProp === "function") {
        onPageChangeProp(totalPages);
      }
      setSelectedRowIndex(0);
    }
  }, [
    filteredItems,
    itemsPerPage,
    currentPage,
    isServerSide,
    totalItemsProp,
    onPageChangeProp,
  ]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1) {
      return;
    }

    const totalPages =
      Math.ceil((isServerSide ? totalItemsProp ?? 0 : filteredItems.length) / itemsPerPage) ||
      1;
    const nextPage = Math.min(pageNumber, totalPages);

    if (isServerSide) {
      onPageChangeProp?.(nextPage);
    } else {
      setCurrentPageState(nextPage);
    }
    setSelectedRowIndex(0);
  };

  const handleItemsPerPageChange = (event) => {
    const nextValue = parseCurrency(event.target.value);
    const nextItemsPerPage = nextValue || 50;
    if (isServerSide) {
      onItemsPerPageChangeProp?.(nextItemsPerPage);
      onPageChangeProp?.(1);
    } else {
      setItemsPerPageState(nextItemsPerPage);
      setCurrentPageState(1);
    }
    setSelectedRowIndex(0);
  };

  const renderPageNumbers = (totalPages, disabled) => {
    const pages = [];
    const maxVisiblePages = 5;
    const range = 2;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
            <a
              href="#"
              className="page-link"
              onClick={(event) => {
                event.preventDefault();
                if (!disabled) {
                  handlePageChange(i);
                }
              }}
              style={pageButtonStyle(currentPage === i, themeColor)}
            >
              {i}
            </a>
          </li>
        );
      }
      return pages;
    }

    pages.push(
      <li key={1} className={`page-item ${currentPage === 1 ? "active" : ""}`}>
        <a
          href="#"
          className="page-link"
          onClick={(event) => {
            event.preventDefault();
            if (!disabled) {
              handlePageChange(1);
            }
          }}
          style={pageButtonStyle(currentPage === 1, themeColor)}
        >
          1
        </a>
      </li>
    );

    if (currentPage - range > 2) {
      pages.push(
        <li key="ellipsis-start" className="page-item">
          <span
            className="page-link"
            style={{
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              color: "#9ca3af",
              backgroundColor: "#fff",
              fontSize: "14px",
              padding: "6px 12px",
              minWidth: "36px",
              textAlign: "center",
            }}
          >
            ...
          </span>
        </li>
      );
    }

    for (
      let i = Math.max(currentPage - range, 2);
      i <= Math.min(currentPage + range, totalPages - 1);
      i++
    ) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
          <a
            href="#"
            className="page-link"
            onClick={(event) => {
              event.preventDefault();
              if (!disabled) {
                handlePageChange(i);
              }
            }}
            style={pageButtonStyle(currentPage === i, themeColor)}
          >
            {i}
          </a>
        </li>
      );
    }

    if (currentPage + range < totalPages - 1) {
      pages.push(
        <li key="ellipsis-end" className="page-item">
          <span
            className="page-link"
            style={{
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              color: "#9ca3af",
              backgroundColor: "#fff",
              fontSize: "14px",
              padding: "6px 12px",
              minWidth: "36px",
              textAlign: "center",
            }}
          >
            ...
          </span>
        </li>
      );
    }

    pages.push(
      <li
        key={totalPages}
        className={`page-item ${currentPage === totalPages ? "active" : ""}`}
      >
        <a
          href="#"
          className="page-link"
          onClick={(event) => {
            event.preventDefault();
            if (!disabled) {
              handlePageChange(totalPages);
            }
          }}
          style={pageButtonStyle(currentPage === totalPages, themeColor)}
        >
          {totalPages}
        </a>
      </li>
    );

    return pages;
  };

  const totalItemsCount = isServerSide
    ? totalItemsProp ?? masterList.length
    : filteredItems.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(() => {
    if (isServerSide) {
      return masterList;
    }
    return filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredItems, indexOfFirstItem, indexOfLastItem, isServerSide, masterList]);

  const resolveRowId =
    typeof getRowId === "function"
      ? getRowId
      : (item, index) => item?.id ?? item?.Id ?? index;

  const triggerRowSelected = (payload) => {
    if (typeof onRowSelected !== "function") {
      return;
    }

    if (payload && typeof payload === "object") {
      onRowSelected(payload);
      return;
    }

    const foundItem = filteredItems.find(
      (item, index) => resolveRowId(item, index) === payload
    );

    if (foundItem) {
      onRowSelected(foundItem);
    }
  };

  const handleRowClick = (rowIndex, item) => {
    if (isLoading) {
      return;
    }
    setSelectedRowIndex(rowIndex);
    triggerRowSelected(item);
  };

  useDataTableKeyboardNavigation(
    !isLoading,
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    triggerRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    totalItemsCount,
    itemsPerPage
  );

  const perPageOptions = useMemo(() => {
    const extra = isServerSide ? [] : [masterList.length];
    return uniqueOptions([...itemsPerPageOptions, ...extra]);
  }, [itemsPerPageOptions, masterList.length, isServerSide]);

  const effectivePlaceholder =
    searchPlaceholder || `ค้นหา${name || "ข้อมูล"}...`;

  const totalPages = Math.ceil(totalItemsCount / itemsPerPage) || 1;
  const isCreateDisabled =
    typeof onCreate !== "function" || Boolean(isLoading);
  const disablePagination = isLoading || totalItemsCount === 0;

  return (
    <>
      <Topbar
        breadcrumbs={[
          { label: name },
        ]}
      />
      {!hidePageTitle && (
        <div className="page-header d-flex justify-content-between align-items-center mb-3" style={{ marginTop: "20px" }}>
          <Breadcrumbs
            page={name}
            items={[
              { name: name, url: entityPath },
            ]}
          />
        </div>
      )}
      {hidePageTitle && <div style={{ marginTop: "20px" }} />}


      <div
        className="card shadow-sm mb-3 border-0"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-md-6 col-lg-4">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder={effectivePlaceholder}
                  className="form-control ps-5"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    padding: "10px 10px 10px 45px",
                    fontSize: "14px",
                  }}
                  value={effectiveSearchTerm}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    if (isServerSide) {
                      onSearchChangeProp?.(nextValue);
                    } else {
                      setLocalSearchTerm(nextValue);
                    }
                  }}
                  disabled={isLoading}
                />
                <i
                  className="fas fa-search position-absolute"
                  style={searchIconStyle}
                ></i>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              {customHeader && (
                <div style={{ minHeight: 56, display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      width: "100%",
                      padding: "10px 0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: 18,
                    }}
                  >
                    {customHeader}
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-6 col-lg-4 d-flex justify-content-end">
              <button
                onClick={onCreate}
                className="btn text-white d-flex align-items-center gap-2"
                style={{
                  backgroundColor: themeColor,
                  borderRadius: "10px",
                  padding: "10px 24px",
                  fontWeight: "500",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(225, 63, 87, 0.2)",
                  transition: "all 0.3s ease",
                  opacity: isCreateDisabled ? 0.6 : 1,
                  pointerEvents: isCreateDisabled ? "none" : "auto",
                }}
                onMouseOver={(event) => {
                  if (isCreateDisabled) {
                    return;
                  }
                  event.currentTarget.style.backgroundColor = "#c9354a";
                  event.currentTarget.style.transform = "translateY(-1px)";
                  event.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(225, 63, 87, 0.3)";
                }}
                onMouseOut={(event) => {
                  if (isCreateDisabled) {
                    return;
                  }
                  event.currentTarget.style.backgroundColor = themeColor;
                  event.currentTarget.style.transform = "translateY(0)";
                  event.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(225, 63, 87, 0.2)";
                }}
                type="button"
              >
                <i className="fa fa-plus"></i>
                <span>สร้าง{name}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="card shadow-sm border-0"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <div className="card-body p-0">
          <div
            className="table-responsive"
            style={{ maxHeight: "600px", overflowY: "auto" }}
          >
            <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
              <thead
                style={{
                  ...tableHeaderStyle,
                  backgroundColor: themeColor,
                }}
              >
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`py-3 ${column.align === "center"
                        ? "text-center"
                        : column.align === "end"
                          ? "text-end"
                          : column.align === "start"
                            ? "text-start"
                            : ""
                        }`}
                      style={{
                        ...headerCellBaseStyle,
                        width: column.width,
                      }}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-5">
                      <div className="d-flex flex-column align-items-center gap-3">
                        <div className="spinner-border text-danger" role="status">
                          <span className="visually-hidden">{loadingMessage}</span>
                        </div>
                        <span style={{ color: "#6b7280", fontWeight: "500" }}>
                          {loadingMessage}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item, index) => {
                    const rowId = resolveRowId(item, index);
                    return (
                      <tr
                        key={rowId}
                        style={{
                          ...getDataTableRowStyle(index, selectedRowIndex),
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                        onClick={() => handleRowClick(index, item)}
                        onMouseEnter={(event) => {
                          if (index !== selectedRowIndex) {
                            event.currentTarget.style.backgroundColor = "#f9fafb";
                          }
                        }}
                        onMouseLeave={(event) => {
                          if (index !== selectedRowIndex) {
                            event.currentTarget.style.backgroundColor = "";
                          }
                        }}
                      >
                        {columns.map((column) => {
                          const alignmentClass =
                            column.align === "center"
                              ? "text-center"
                              : column.align === "end"
                                ? "text-end"
                                : column.align === "start"
                                  ? "text-start"
                                  : "";

                          const isActionColumn = Boolean(column.isAction);
                          const columnContext = {
                            startIndex: indexOfFirstItem,
                            itemIndex: index,
                            triggerRowSelected,
                            extraData,
                          };

                          const content = column.render
                            ? column.render(item, index, columnContext)
                            : item?.[column.key] ?? "-";

                          return (
                            <td
                              key={column.key}
                              className={`${alignmentClass} ${column.bodyClassName || ""
                                }`.trim()}
                              style={{
                                verticalAlign: isActionColumn ? "middle" : "top",
                                color: isActionColumn ? undefined : "#111827",
                                padding: "12px",
                              }}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-5">
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: "200px" }}
                      >
                        <i
                          className="fas fa-inbox"
                          style={{
                            fontSize: "48px",
                            color: "#d1d5db",
                            marginBottom: "16px",
                          }}
                        ></i>
                        <h5 style={{ color: "#6b7280", fontWeight: "500" }}>
                          ไม่พบข้อมูล
                        </h5>
                        <p
                          style={{
                            color: "#9ca3af",
                            fontSize: "14px",
                            marginTop: "8px",
                          }}
                        >
                          ไม่มี{name}ในระบบ
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && currentItems.length > 0 && (
            <div
              className="px-4 py-3"
              style={{
                backgroundColor: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                <div style={{ minWidth: 0 }} className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                  <small className="text-muted text-truncate" style={{ fontSize: "13px", maxWidth: 520 }}>
                    {getDataTableKeyboardInstructions(false, true)}
                  </small>

                  <div className="d-flex align-items-center gap-2" style={{ marginLeft: 0 }}>
                    <label
                      htmlFor="itemsPerPage"
                      className="mb-0 d-none d-sm-block"
                      style={{ fontSize: "14px", color: "#6b7280" }}
                    >
                      แสดง:
                    </label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="form-select"
                      style={{
                        width: "auto",
                        minWidth: "80px",
                        fontSize: "14px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        padding: "6px 12px",
                      }}
                    >
                      {perPageOptions.map((option) => (
                        <option key={option} value={option}>
                          {option === masterList.length ? "ทั้งหมด" : option}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: "14px", color: "#6b7280" }}>
                      รายการ (ทั้งหมด {totalItemsCount} รายการ)
                    </span>
                  </div>
                </div>

                <nav>
                  <ul className="pagination mb-0 d-flex" style={{ gap: "6px", alignItems: 'center' }}>
                    <li
                      className={`page-item ${currentPage === 1 || disablePagination ? "disabled" : ""}`}
                    >
                      <a
                        href="#"
                        className="page-link"
                        onClick={(event) => {
                          event.preventDefault();
                          if (!disablePagination && currentPage > 1) {
                            handlePageChange(currentPage - 1);
                          }
                        }}
                        style={{
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          color: "#374151",
                          fontSize: "14px",
                          padding: "6px 12px",
                        }}
                      >
                        ก่อนหน้า
                      </a>
                    </li>

                    {renderPageNumbers(totalPages, disablePagination)}

                    <li
                      className={`page-item ${currentPage === totalPages || disablePagination ? "disabled" : ""}`}
                    >
                      <a
                        href="#"
                        className="page-link"
                        onClick={(event) => {
                          event.preventDefault();
                          if (!disablePagination && currentPage < totalPages) {
                            handlePageChange(currentPage + 1);
                          }
                        }}
                        style={{
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          color: "#374151",
                          fontSize: "14px",
                          padding: "6px 12px",
                        }}
                      >
                        ถัดไป
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InitialDataListPage;
